/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react'
import {
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Collapse,
  Box,
  Typography,
  FormHelperText,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { DataCombinationType, ChartType } from '~/interfaces/IStatistics'
import { useLang } from '~/hooks/useLang'

interface AddChartSectionProps {
  readonly onAdd: (
    dataCombination: DataCombinationType,
    chartType: ChartType
  ) => void
  readonly disabled?: boolean
  readonly hidden?: boolean
  readonly existingCharts: ReadonlyArray<{
    readonly dataCombination: DataCombinationType
    readonly chartType: ChartType
  }>
}

export default function CustomDashboardAddChartSection({
  onAdd,
  disabled = false,
  hidden = true,
  existingCharts,
}: AddChartSectionProps) {
  const { translate } = useLang()
  const [dataCombination, setDataCombination] = useState<
    DataCombinationType | ''
  >('')
  const [chartType, setChartType] = useState<ChartType | ''>('')
  const [error, setError] = useState<string>('')

  const dataCombinationOptions = [
    {
      value: DataCombinationType.DISPATCH_BY_STATUS,
      label: 'Total de Despachos por Status',
    },
    {
      value: DataCombinationType.DEVICE_BY_STATUS,
      label: 'Total de Dispositivos por Status',
    },
    {
      value: DataCombinationType.DISPATCH_BY_TYPE,
      label: 'Total de Despachos por Tipo',
    },
    {
      value: DataCombinationType.DISPATCH_BY_SUBTYPE,
      label: 'Total de Despachos por Subtipo',
    },
    {
      value: DataCombinationType.DISPATCH_BY_CLOSED_STATUS,
      label: 'Total de Despachos por Status de Fechamento',
    },
    {
      value: DataCombinationType.DISPATCH_BY_DISPATCH_GROUP,
      label: 'Total de Despachos por Grupo de Despacho',
    },
    {
      value: DataCombinationType.DISPATCH_BY_CITY,
      label: 'Total de Despachos por Cidade',
    },
    {
      value: DataCombinationType.DISPATCH_BY_DISTRICT,
      label: 'Total de Despachos por Bairro',
    },
    {
      value: DataCombinationType.DISPATCH_BY_DEVICE,
      label: 'Total de Despachos por Dispositivo',
    },
    {
      value: DataCombinationType.DISPATCHES_BY_HOURS,
      label: 'Total de Despachos por Intervalo de Hora',
    },
    {
      value: DataCombinationType.DISPATCHES_BY_WEEKDAY,
      label: 'Total de Despachos por Dia da Semana',
    },
  ]

  const chartTypeOptions = [
    { value: ChartType.NUMERIC, label: 'Indicador Numérico' },
    { value: ChartType.BAR, label: 'Barras' },
    { value: ChartType.COLUMN, label: 'Colunas' },
    { value: ChartType.PIE, label: 'Circular' },
    { value: ChartType.LINE, label: 'Linha' },
  ]

  const handleAdd = () => {
    if (dataCombination && chartType) {
      // Verifica se já existe a mesma combinação
      const isDuplicate = existingCharts.some(
        (chart) =>
          chart.dataCombination === dataCombination &&
          chart.chartType === chartType
      )

      if (isDuplicate) {
        setError('Este gráfico com esses dados já foi adicionado')
        return
      }

      setError('')
      onAdd(dataCombination as DataCombinationType, chartType as ChartType)
      setDataCombination('')
      setChartType('')
    }
  }

  const handleDataCombinationChange = (
    event: SelectChangeEvent<DataCombinationType | ''>
  ) => {
    setDataCombination(event.target.value as DataCombinationType)
  }

  const handleChartTypeChange = (event: SelectChangeEvent<ChartType | ''>) => {
    setChartType(event.target.value as ChartType)
  }

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          paddingX: 2,
          paddingBottom: 2,
        }}
      >
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid item xs={12} sm={6} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>{translate('Tipo de Gráfico')}</InputLabel>
              <Select
                value={chartType}
                label={translate('Tipo de Gráfico')}
                onChange={handleChartTypeChange}
                disabled={disabled}
              >
                {chartTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {translate(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={5}>
            <FormControl fullWidth size="small" error={Boolean(error)}>
              <InputLabel>{translate('Combinação de Dados')}</InputLabel>
              <Select
                value={dataCombination}
                label={translate('Combinação de Dados')}
                onChange={handleDataCombinationChange}
                disabled={disabled}
              >
                {dataCombinationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {translate(option.label)}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{translate(error)}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={2} display="flex" alignItems="flex-start">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={disabled || !dataCombination || !chartType}
              fullWidth
              sx={{ height: '40px' }}
            >
              {translate('Adicionar')}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                padding: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {translate(
                  'Os gráficos serão gerados quando você aplicar os filtros'
                )}
              </Typography>
              <Typography
                variant="body2"
                color="warning.main"
                sx={{ fontStyle: 'italic', mt: 1, fontWeight: 'bold' }}
              >
                {translate(
                  'Importante: Ao adicionar novos gráficos, clique novamente em "Aplicar Filtros" para gerar os dados'
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Collapse>
  )
}
